/** Programme: next-SAT card, Year 0–15 rail, saved records and guarantee documents. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { api, download } from "../api/client";
import { keys, useHistory, useMe, useProgramme, useProject } from "../api/hooks";
import type { Programme, ProgrammeYear } from "../api/types";
import { can } from "../components/AppShell";
import { HistoryOverview, NextSatCard, ProgrammeRail, RecordComparison, StageIntro } from "../components/sat";
import { ConfirmDialog, ErrorNotice, FileDrop, Notice, Spinner } from "../components/ui";
import { uploadFile } from "../lib/files";
import { bytes, dateTime, day, localText, num, yearLabel } from "../lib/format";

interface EvidenceLink {
  filename: string;
  size: number;
  sha256: string;
  url: string;
}

function EvidenceFiles({ pid, year }: { pid: string; year: number }) {
  const [open, setOpen] = useState(false);
  const files = useQuery({
    queryKey: ["evidence-links", pid, year],
    queryFn: () => api.get<EvidenceLink[]>(`/projects/${pid}/years/${year}/evidence`),
    enabled: open,
    staleTime: 60_000, // signed URLs are short-lived
  });
  return (
    <details className="disclosure" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>Raw evidence of this result</summary>
      <div>
        {files.isLoading && <Spinner label="Listing evidence" />}
        <ErrorNotice error={files.error} />
        {files.data && files.data.length === 0 && <p className="muted small">The evidence batch is no longer available.</p>}
        <ul className="file-list">
          {files.data?.map((file) => (
            <li key={file.sha256}>
              <span>{file.filename}</span>
              <span className="row">
                <span className="muted small">{bytes(file.size)}</span>
                <a className="btn small" href={file.url} rel="noopener">
                  Download
                </a>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}

function ArchivedVersions({ pid, year, timeZone }: { pid: string; year: number; timeZone: string }) {
  const [open, setOpen] = useState(false);
  const history = useHistory(pid, year, open);
  return (
    <details className="disclosure" onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>Archived versions</summary>
      <div>
        {history.isLoading && <Spinner label="Loading history" />}
        <ErrorNotice error={history.error} />
        {history.data && history.data.length === 0 && <p className="muted small">Nothing has been overwritten or deleted for this year.</p>}
        <ul className="file-list">
          {history.data?.map((item) => (
            <li key={item.archived_at}>
              <span>
                <strong>{item.status}</strong> · RTE {num(item.measured_rte_pct)}% · CUE {num(item.cue_mwh)} MWh
                <br />
                <span className="muted small">
                  {item.archived_reason === "deleted" ? "Deleted" : "Overwritten"} {dateTime(item.archived_at, timeZone)}
                </span>
              </span>
              <Link className="btn small" to={`/p/${pid}/runs/${item.run_id}`}>
                Open
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}

function YearRecord(props: { pid: string; programme: Programme; year: ProgrammeYear; editor: boolean; canDownload: boolean }) {
  const { pid, programme, year, editor } = props;
  const client = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const record = year.record;
  return (
    <div className="stack">
      <RecordComparison year={year.year} commitment={year.commitments} record={record} />
      {record ? (
        <div className="card stack">
          <div className="row between">
            <div>
              <strong>SAT window</strong>
              <div className="muted small">
                {localText(record.sat_start_local as string)} – {localText(record.sat_end_local as string)} ({programme.timezone})
              </div>
              <div className="muted small">
                {String(record.battery_count)} batteries · {String(record.required_cycles)} required cycle(s) · saved{" "}
                {dateTime(record.saved_at as string, programme.timezone)}
              </div>
            </div>
            <div className="row">
              <Link className="btn primary" to={`/p/${pid}/runs/${record.run_id}`}>
                Open assessment
              </Link>
              {props.canDownload && (
                <button
                  className="btn"
                  onClick={() => download(`/projects/${pid}/years/${year.year}/report`).catch(setError)}
                >
                  Download report (PDF)
                </button>
              )}
              {editor && (
                <ConfirmDialog
                  trigger={<button className="btn danger">Delete result</button>}
                  title={`Delete the ${yearLabel(year.year)} result?`}
                  description="The result is archived and can be reopened from the archived versions. The evidence and reports are kept."
                  confirmLabel="Delete result"
                  danger
                  onConfirm={async () => {
                    await api.delete(`/projects/${pid}/years/${year.year}/result?version=${programme.results_version}`);
                    await Promise.all([
                      client.invalidateQueries({ queryKey: keys.programme(pid) }),
                      client.invalidateQueries({ queryKey: keys.project(pid) }),
                      client.invalidateQueries({ queryKey: keys.projects }),
                      client.invalidateQueries({ queryKey: keys.runs(pid) }),
                      client.invalidateQueries({ queryKey: keys.history(pid, year.year) }),
                    ]);
                  }}
                />
              )}
            </div>
          </div>
          {record.note ? (
            <p className="small">
              <strong>Report note:</strong> {String(record.note)}
            </p>
          ) : null}
          <ErrorNotice error={error} />
          {props.canDownload && <EvidenceFiles pid={pid} year={year.year} />}
        </div>
      ) : (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            No result is saved for {yearLabel(year.year)}
            {year.due_date ? ` (due ${day(year.due_date)})` : ""}.
            {editor ? " Upload the SAT evidence, run the assessment, then save it to this year." : ""}
          </p>
        </div>
      )}
      {editor && <ArchivedVersions pid={pid} year={year.year} timeZone={programme.timezone} />}
    </div>
  );
}

function Guarantees({ pid, programme, editor, canDownload }: { pid: string; programme: Programme; editor: boolean; canDownload: boolean }) {
  const client = useQueryClient();
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  async function add(file: File) {
    setError(null);
    try {
      const uploaded = await uploadFile(`/projects/${pid}/uploads`, file, "guarantee", {}, setProgress);
      setProgress("Reading the guarantee…");
      await api.post(`/projects/${pid}/guarantees`, { sha256: uploaded.sha256, filename: file.name });
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.programme(pid) }),
        client.invalidateQueries({ queryKey: keys.project(pid) }),
      ]);
    } catch (reason) {
      setError(reason);
    } finally {
      setProgress(null);
    }
  }

  return (
    <section className="card stack">
      <div className="card-head">
        <div>
          <h3>Performance guarantee</h3>
          <p>The contractual schedule every assessment is compared with.</p>
        </div>
      </div>
      <ul className="file-list">
        {programme.guarantees.map((document) => (
          <li key={String(document.sha256)}>
            <span>
              {String(document.filename)}
              <br />
              <span className="muted small">
                {document.page_count ? `${document.page_count} pages · ` : ""}added {dateTime(document.uploaded_at as string, programme.timezone)}
              </span>
            </span>
            {canDownload && (
              <button className="btn small" onClick={() => download(`/projects/${pid}/guarantees/${document.sha256}`).catch(setError)}>
                Download
              </button>
            )}
          </li>
        ))}
      </ul>
      {editor && (
        <FileDrop
          accept="application/pdf"
          disabled={!!progress}
          onFiles={(files) => files[0] && add(files[0])}
          label={
            progress ? (
              <Spinner label={progress.replace(/…$/, "")} />
            ) : (
              <span className="small">
                <strong>Add or replace the guarantee</strong> — drop a PDF; the schedule is read again from it.
              </span>
            )
          }
        />
      )}
      <ErrorNotice error={error} title="The guarantee could not be added" />
      <details className="disclosure">
        <summary>Committed values by programme year</summary>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Year</th>
                <th>Due</th>
                <th>CUEP</th>
                <th>CRTE</th>
                <th>CUE</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {programme.years.map((year) => {
                const commitments = year.commitments as Record<string, any> | null | undefined;
                return (
                  <tr key={year.year}>
                    <td>{year.compact_label}</td>
                    <td>{day(year.due_date)}</td>
                    <td className="num">{num(commitments?.cuep_pct)}%</td>
                    <td className="num">{num(commitments?.crte_pct)}%</td>
                    <td className="num">{num(commitments?.committed_cue_mwh)} MWh</td>
                    <td>{commitments?.source === "override" ? "Override" : "Guarantee"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

export function ProgrammePage() {
  const { pid = "" } = useParams();
  const me = useMe();
  const project = useProject(pid);
  const programme = useProgramme(pid);
  const [picked, setPicked] = useState<number | null>(null);
  const editor = can(me.data, "evidence.upload");

  if (programme.isLoading || project.isLoading) return <Spinner label="Loading the programme" />;
  if (programme.error || project.error) return <ErrorNotice error={programme.error ?? project.error} title="The project could not be opened" />;
  const data = programme.data!;
  const saved = data.years.filter((year) => year.record);
  const latest = saved.length ? saved[saved.length - 1] : null;
  const nextDue = data.next_due as { year: number; due_date: string; days_remaining: number } | null | undefined;
  const selectedYear = picked ?? latest?.year ?? nextDue?.year ?? 0;
  const selected = data.years.find((year) => year.year === selectedYear) ?? data.years[0];
  const lastYear = data.years.length ? data.years[data.years.length - 1].year : 15;

  return (
    <div className="stack" style={{ gap: "1.4rem" }}>
      <StageIntro
        title={project.data!.label}
        description={
          data.commissioning_date
            ? `Commissioning test ${day(data.commissioning_date)}${data.commissioning_date_source === "derived" ? " (from the saved Year 0 result)" : ""}. Dates and times are shown in ${data.timezone}.`
            : `The commissioning test date is not set yet. Dates and times are shown in ${data.timezone}.`
        }
        status={project.data!.client_name ?? "Programme"}
      />
      <NextSatCard
        nextDue={nextDue}
        hasCommissioningDate={!!data.commissioning_date}
        savedCount={saved.length}
        lastYear={lastYear}
        action={
          editor ? (
            <>
              {!data.commissioning_date && (
                <Link className="btn" to={`/p/${pid}/settings`}>
                  Set the commissioning date
                </Link>
              )}
              <Link className="btn primary" to={`/p/${pid}/evidence`}>
                Add SAT evidence
              </Link>
            </>
          ) : undefined
        }
      />
      <HistoryOverview
        saved={saved.length}
        latest={latest ? yearLabel(latest.year, true) : "None yet"}
        next={nextDue ? `${yearLabel(nextDue.year, true)} · ${day(nextDue.due_date)}` : "—"}
      />
      {data.years.length === 0 ? (
        <Notice kind="warning">The guarantee has no schedule for this project's CPD. Check the project settings.</Notice>
      ) : (
        <>
          <ProgrammeRail years={data.years} selected={selected.year} nextYear={nextDue?.year ?? null} onSelect={setPicked} />
          <YearRecord pid={pid} programme={data} year={selected} editor={editor} canDownload={can(me.data, "download.exports")} />
        </>
      )}
      {data.year0_definition && (
        <p className="sat-section-note">
          <strong>Year 0 in the guarantee:</strong> {data.year0_definition}
        </p>
      )}
      <Guarantees pid={pid} programme={data} editor={can(me.data, "project.edit")} canDownload={can(me.data, "download.evidence")} />
    </div>
  );
}
